/**
 * フォームの状態管理のためのstate/booleanと
 * 全てのinput共通で利用するハンドラ関数の作成を担う
 * 
 * [利用の際に食わせる第一引数schemaに関して]
 * Formで利用するinputの内容全てを宣言的に記述した、FormItemの配列を食わせる。
 * その配列をreactiveObjectに変換、現在のバリデーション状態を示すプロパティstatusを加えて
 * 利用者側に返却、返却されたstateをviewでそのまま利用する形を想定。
 * 後述のhandleChangeイベントにより、FormItem.valueの値が書き換わる形
 * 
 * [帰ってくるハンドラ関数2種に関して]
 * 全てのinput要素で同じハンドラ関数を渡す
 * e.g. 
 * <input name={item.name} onChange={handleChange} onBlur={handleBlur}/>
 * <textarea name={item.name} onChange={handleChange} onBlur={handleBlur}/>
 */
import React, { useState, useReducer, useEffect } from 'react';

export const useForm = (
  schema: FormItem[]
): UseForm => {

  // statusを付与、初期化する
  const initialState = schema.map(item => {
    const status = item.validate ? 'initial' : 'pass';
    return { ...item, status } as FormItem;
  });

  const [state, dispatch] = useReducer(reducer, initialState);

  // change時、値の更新
  const handleChange = (
    event: React.ChangeEvent<HTMLInputElement|HTMLSelectElement|HTMLTextAreaElement>
  ) => {
    event.stopPropagation();

    const name = event.target.getAttribute('name') as string;
    const type = event.target.getAttribute('type') as string;
    const index = state.findIndex(item => item.name === name);
    const value = event.target.value;

    if (type === "checkbox") {
      const target = event.target as { checked: boolean }; // sorry..
      const checked = target.checked;
      dispatch({ type: "update-checkbox", name, value, index, checked });
    } else {
      dispatch({ type: "update", name, value, index });
    }
    dispatch({ type: "validate", name, value, index });
  };

  // blur時、バリデーションを実施する
  const handleBlur = (
    event: React.ChangeEvent<HTMLInputElement|HTMLTextAreaElement|HTMLSelectElement>
  ) => {
    event.stopPropagation();

    const name = event.target.getAttribute('name') as string;
    const index = state.findIndex(item => item.name === name);
    const value = event.target.value;

    dispatch({ type: "validate", name, value, index });
  };

  // submitのdisableに利用
  const [dependency, setDependency] = useState(state.map(item=>item.status));
  useEffect(() => {
    setDependency(state.map(item=>item.status));
  }, [state]);
  const [isAllValid, setIsAllValid] = useState(false);
  useEffect(() => {
    setIsAllValid(dependency.every(item => item === 'pass'));
  }, [dependency]);

  // schemaが更新された際の処理
  useEffect(() => {
    dispatch({ type: "update-schema", schema });
  }, [schema]);

  return [
    { state, isAllValid },
    { handleChange, handleBlur }
  ];
};

// consts 

const reducer: React.Reducer<FormItem[], Action | ActionSchemaUpdate> = (state, action) => {

  switch (action.type) {
    case "update": {
      const value = action.value;
      return [
        ...state.slice(0, action.index),
        { ...state[action.index], value },
        ...state.slice(action.index + 1),
      ]
    }
    case "update-checkbox": {
      const items = state[action.index].items as FormStateCheckboxItem[];
      const checkboxIndex = items.findIndex(item => item.value === action.value);
      const nextItems = [
        ...items.slice(0, checkboxIndex),
        { ...items[checkboxIndex], checked: !items[checkboxIndex].checked },
        ...items.slice(checkboxIndex + 1),
      ]

      const value = nextItems.filter(item => item.checked).map(item => item.value);

      return [
        ...state.slice(0, action.index),
        { ...state[action.index], value, items: nextItems },
        ...state.slice(action.index + 1),
      ]
    }
    case "update-schema": {
      if (!action.schema) return state;
      return action.schema.map((item, index) => {
        const validate = item.validate;
        const isThrough = typeof validate === 'undefined';
        const isValid = validate ? validate(item.value, item, state) : true;
        const status = (isThrough || isValid) ? 'pass' : 'error';

        item.value = state[index].value;
        item.status = status;
        return item;
      });
    }
    case "validate": {
      const myself = state[action.index];
      const validate = myself.validate;

      const isThrough = typeof myself.validate === 'undefined';
      const isValid = validate ? validate(myself.value, myself, state) : true;

      const status = (isThrough || isValid) ? 'pass' : 'error';

      return [
        ...state.slice(0, action.index),
        { ...state[action.index], status },
        ...state.slice(action.index + 1),
      ]
    }
    default: throw new Error();
  }
}

// types

export type FormItem = {
  // inputの種類
  type: 'text' | 'email' | 'number' | 'checkbox'| 'select' | 'textarea' | 'hidden'
  // input名
  name: string
  // 操作される値。デフォルト値もここで指定する
  // checkboxの場合のみstring[]を想定
  value: InputValue
  // 名前の通り。type = /^(checkbox|select)$/の場合は無視する
  placeholder?: string
  // blurの際に適応するバリデーション。
  // こちらがundefinedの場合は、入力必須事項にならない(ので、入力必須にしたい場合は必ず設定する)
  validate?: (value: InputValue, myself?: FormItem, state?: FormItem[]) => boolean
  // エラー時のメッセージ(処理では利用しない)
  error?: string
  // checkboxまたはselectの場合にこちらを利用する
  items?: FormStateCheckboxItem[] | FormStateSelectItem[]
  // カスタムフックの中で動的に付与される値。
  // 現在のバリデーション情報が入り、一度もblurが発生していない場合にはinitialが入る
  status?: 'initial' | 'pass' | 'error'
}

// checkboxの場合は配列で値を保有、それ以外はstring|numberを想定
type InputValue = string | number | string[];

export type FormStateCheckboxItem = {
  checked: boolean,
  value: string
}
export type FormStateSelectItem = {
  name: string,
  value: string
}

type Action = {
  type: "update" | "update-checkbox" | "validate"
  name: string
  value: string | number
  index: number
  checked?: boolean
}

type ActionSchemaUpdate = {
  type: "update-schema"
  schema?: FormItem[]
}

export type OnChangeFormInput = 
  (event: React.ChangeEvent<HTMLInputElement|HTMLTextAreaElement|HTMLSelectElement>) => void
export type OnBlurFormInput = 
  (event: React.ChangeEvent<HTMLInputElement|HTMLTextAreaElement|HTMLSelectElement>) => void

type UseForm = [
  {
    state: FormItem[]
    isAllValid: boolean,
  },
  {
    handleChange: OnChangeFormInput
    handleBlur: OnBlurFormInput
  }
]